import Model from "./model";
import { clone, flatten, groupBy, normalize, timeout, uniq } from "../utils";
import Notification from "../singleton/notification";
import ListView from "../singleton/list-view";
import LinkedField from "../singleton/linked-field";
import Field from "../singleton/field";
import Feedback from "../singleton/feedback";
import Entity from "../singleton/entity";
import Documents from "../singleton/documents";
import DocumentValue from "../singleton/document-value";
import CollectionTotals from "../singleton/collection-totals";
import CollectionState from "../singleton/collection-state";
import CollectionSorting from "../singleton/collection-sorting";
import CollectionFilters from "../singleton/collection-filters";
import IRelation from "../interface/relation";
import IModelRelatedInfo from "../interface/model-related-info";
import IModelRelatedData from "../interface/model-related-data";
import IListViewState from "../interface/list-view-state";
import IListView from "../interface/list-view";
import ILinkedInactive from "../interface/linked-inactive";
import ILinkedFieldOption from "../interface/linked-field-option";
import IGroup from "../interface/grouped";
import IFieldSortingPrepared from "../interface/field-sorting-prepared";
import IField from "../interface/field";
import IFeedbackResult from "../interface/feedback-result";
import IFeedbackKey from "../interface/feedback-key";
import IEntityFilterPrepared from "../interface/entity-filter-prepared";
import IEntityFilter from "../interface/entity-filter";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ICollectionResponse from "../interface/collection-response";
import ICollectionArgs from "../interface/collection-args";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";

export default class CollectionClass {

    private entity: IEntity;

    private view: IListView;

    private listId: number | null;

    private relation: IRelation | null = null;

    private recordId: number | null = null;

    private models: Model[] = [];

    private state: IListViewState;

    private dataLoaded: boolean = false;

    private recordTitle: string = '';

    private textPattern: string = '';

    private baseModel: IDocument = {};

    private active: boolean = true;

    private feedback: IFeedbackKey | null = null;

    private relatedEntityIds: { [entityId: number]: number[] } = {};

    constructor(cfg: ICollectionArgs) {
        this.entity = cfg.entity;
        this.listId = cfg.listId;
        if (cfg.relation) {
            this.relation = cfg.relation;
            this.recordId = cfg.recordId;
        }
        this.baseModel = Documents.getEmpty(this.entity);
        this.view = this.getView(cfg.viewId);
        this.state = this.getViewState();
        this.setRelatedEntities();
        this.init();
    }

    /* Public Methods */

    public getClientResponse(): ICollectionResponse {
        let records: IDocument[] = [];
        if (this.dataLoaded) {
            records = this.models.filter((model: Model) => {
                return model.filtered;
            }).map((model: Model) => model.data);
        }
        return {
            records: clone(records),
            isLoading: !this.dataLoaded,
            count: this.models.length,
            state: this.getState(),
            totals: CollectionTotals.get(this.entity.id, records),
            entityId: this.entity.id,
            viewId: this.view.id as number,
            listId: this.listId,
            relationId: this.relation ? this.relation.id : null,
            recordId: this.recordId,
            recordTitle: this.recordTitle,
            textPattern: this.textPattern,
            feedback: this.feedback
        };
    }

    public setView(viewId: number): void {
        this.view = this.getView(viewId);
        this.applyView();
        this.notification();
    }

    public clearState(): void {
        this.state = {
            filters: [],
            sorting: [],
            fieldsHidden: [],
            fieldsReadonly: [],
            fieldsOrder: [],
            fieldsWidths: [],
            fieldLocked: null
        };
    }

    public getEntityId(): number {
        return this.entity.id;
    }

    /* Public Methods - State control */

    public async setFilters(filters: IEntityFilter[], save: boolean): Promise<void> {
        this.state.filters = CollectionFilters.process(filters);
        this.applyFilters();
        this.applySorting();
        this.notification();
        if (save && !this.view.isGlobal) {
            await this.timeout();
            await ListView.saveState(this.view.id as number, this.state);
        }
    }

    public async toggleFieldSort(fieldId: number, save: boolean): Promise<void> {
        this.state = CollectionState.toggleFieldSort(this.entity.id, this.state, fieldId);
        this.applySorting();
        this.notification();
        if (save && !this.view.isGlobal) {
            await this.timeout();
            await ListView.saveState(this.view.id as number, this.state);
        }
    }

    public async setFieldsOrder(order: number[]): Promise<void> {
        this.state.fieldsOrder = CollectionState.validateFields(this.entity.id, order);
        this.notification();
        await this.timeout();
        await ListView.saveState(this.view.id as number, this.state);
    }

    public async setFieldWidth(fieldId: number, width: number): Promise<void> {
        this.state = CollectionState.setFieldWidth(this.entity.id, this.state, fieldId, width);
        this.notification();
        await this.timeout();
        await ListView.saveState(this.view.id as number, this.state);
    }

    public async toggleFieldHidden(fieldId: number): Promise<void> {
        this.state = CollectionState.toggleFieldHidden(this.entity.id, this.state, fieldId);
        this.notification();
        await this.timeout();
        await ListView.saveState(this.view.id as number, this.state);
    }

    public async toggleFieldReadonly(fieldId: number): Promise<void> {
        this.state = CollectionState.toggleFieldReadonly(this.entity.id, this.state, fieldId);
        this.notification();
        await this.timeout();
        await ListView.saveState(this.view.id as number, this.state);
    }

    public async toggleLocked(fieldId: number): Promise<void> {
        this.state.fieldLocked = this.state.fieldLocked === fieldId ? null : fieldId;
        this.notification();
        await this.timeout();
        await ListView.saveState(this.view.id as number, this.state);
    }

    public async setTextFilter(textPattern: string): Promise<void> {
        this.textPattern = textPattern;
        this.applyFilters();
        this.notification();
    }

    /* Public Methods - Documents control */

    public async documentCreate(): Promise<IDocument> {
        let created: IDocument;
        if (this.relation && this.recordId) {
            const doc: IDocument = {};
            doc[`c${this.relation.id}`] = [this.recordId];
            created = await Documents.create(this.entity.id, doc);
        } else {
            created = await Documents.create(this.entity.id);
        }
        return (new Model(this.entity, created, this.baseModel)).data;
    }

    public async documentUpdate(id: number, fieldId: number, value: IDocumentValue): Promise<IDocument> {
        const model: Model | null = this.get(id);
        if (model) {
            return await Documents.update(model.getRaw(), this.entity.id, fieldId, value);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async documentDelete(ids: number[]): Promise<number[]> {
        return await Documents.delete(this.entity.id, ids);
    }

    public async documentBulkUpdate(ids: number[], fieldId: number, value: IDocumentValue): Promise<IDocument[]> {
        const result: IDocument[] = [];
        for (let i = 0; i < ids.length; i++) {
            try {
                // TODO - Replace with single call, should be implemented by Nico
                const doc: IDocument = await this.documentUpdate(ids[i], fieldId, value);
                result.push(doc);
            } catch (e) {}
        }
        return result;
    }

    public getDefaultValue(id: number, fieldId: number): IDocumentValue | null {
        const model: Model | null = this.get(id);
        const ref: number[] | null = Field.getDefaultValue(fieldId);
        if (model && ref) {
            return DocumentValue.getByPathSync(model.data, ref);
        }
        return null;
    }

    /* Public Methods - Feedback */

    public async setFeedback(id: number): Promise<void> {
        const feedback: IFeedbackKey | null = Feedback.getKey(id);
        if (feedback) {
            const result: IFeedbackResult[] = await Feedback.getResult(feedback.id)
            this.clearModelsFeedback();
            this.feedback = feedback;
            result.forEach((item: IFeedbackResult) => {
                const model: Model | null = this.get(item.documentId);
                if (model) {
                    model.addFeedback(item);
                }
            });
            this.notification();
        } else {
            this.clearModelsFeedback();
            this.feedback = null;
            this.notification();
        }
    }

    public async resetFeedback(): Promise<void> {
        if (this.feedback) {
            await this.setFeedback(this.feedback.id);
        }
    }

    /* Public Methods - Documents notification */

    public onDocumentsCreate(docs: IDocument[]): void {
        if (this.relation && this.recordId) {
            const pointerId: number = this.relation.id;
            docs = docs.filter((doc: IDocument) => {
                if (doc[`c${pointerId}`] instanceof Array) {
                    return (doc[`c${pointerId}`] as number[])[0] === this.recordId;
                }
                return false;
            });
        }
        if (docs.length) {
            docs.forEach((doc: IDocument) => {
                this.models.push(new Model(this.entity, doc, this.baseModel))
            });
            this.notification();
        }
    }

    public async onDocumentsUpdate(docs: IDocument[]): Promise<void> {
        const relatedInfo: IModelRelatedInfo[][] = [];
        docs.forEach((doc: IDocument) => {
            const model: Model | null = this.get(doc.id as number);
            if (model) {
                model.setData(doc);
                relatedInfo.push(model.checkRelatedData());
            }
        });
        await this.checkRelations(flatten(relatedInfo));
        docs.forEach((doc: IDocument) => {
            const model: Model | null = this.get(doc.id as number);
            if (model) {
                model.calcFormulas();
                model.resetFormatted();
            }
        });
        Documents.setLinkedFieldValues(this.entity, this.models.map((m: Model) => m.data));
        this.applyFilters();
        this.applySorting();
        this.notification();
    }

    public onDocumentsDelete(docs: IDocument[]): void {
        const ids: number[] = docs.map((doc: IDocument) => doc.id as number);
        this.models = this.models.filter((m: Model) => !ids.includes(m.id));
        this.notification();
    }

    public async onRelatedDocuments(entityId: number, docs: IDocument[]): Promise<void> {
        if (this.relatedEntityIds[entityId]) {
            const models: Model[] = [];
            docs.forEach((doc: IDocument) => {
                this.relatedEntityIds[entityId].forEach((pointerId: number) => {
                    this.models.forEach((model: Model) => {
                        const val: number[] = DocumentValue.getLinked(model.data[`c${pointerId}`]);
                        if (val.length && val[0] === doc.id) {
                            model.data[`d${pointerId}`] = [doc];
                            model.calcFormulas();
                            models.push(model);
                        }
                    });
                });
            });
            Documents.setLinkedFieldValues(this.entity, this.models.map((m: Model) => m.data));
            models.forEach((model: Model) => model.resetFormatted());
        }
    }

    public async onEntityReset(): Promise<void> {
        const entity: IEntity | null = Entity.get(this.entity.id);
        if (entity) {
            this.entity = entity;
            this.setRelatedEntities();
            this.baseModel = Documents.getEmpty(this.entity);
            this.view = this.getView(this.view.id as number);
            this.state = this.getViewState();
            const relatedInfo: IModelRelatedInfo[][] = [];
            this.models.forEach((model: Model) => {
                model.resetEntity(this.entity, this.baseModel)
                relatedInfo.push(model.checkRelatedData());
            });
            await this.checkRelations(flatten(relatedInfo));
            this.models.forEach((model: Model) => {
                model.calcFormulas();
                model.resetFormatted();
            });
            Documents.setLinkedFieldValues(this.entity, this.models.map((m: Model) => m.data));
            this.notification();
        }
    }

    /* Public Methods - Linked Field calls */

    public isLinkedActive(fieldId: number, recordId: number): ILinkedInactive[] {
        const model: Model | null = this.get(recordId);
        if (model) {
            return LinkedField.isActive(fieldId, model.getRaw());
        }
        return [];
    }

    public async getLinkedOptions(fieldId: number, recordId: number): Promise<ILinkedFieldOption[]> {
        const model: Model | null = this.get(recordId);
        if (model) {
            return await LinkedField.getOptions(fieldId, model.getRaw());
        } else {
            return [];
        }
    }

    /* Public Methods - Special calls */

    public async getSimilarValues(fieldId: number, recordId: number, pattern: string): Promise<string[]> {
        const ptrn: string = normalize(pattern.toLowerCase().trim());
        const result: string[] = [];
        this.models.forEach((m: Model) => {
            const value: IDocumentValue = m.data[`c${fieldId}`];
            if (m.id !== recordId && value && typeof value === 'string') {
                const formatted: string = normalize(value.toLowerCase().trim());
                if (formatted.startsWith(ptrn)) {
                    result.push(value);
                }
            }
        });
        return uniq(result);
    }

    /* Public Methods - System */

    public clear(): void {
        this.active = false;
    }

    public same(cfg: ICollectionArgs): boolean {
        if (cfg.entity.id !== this.entity.id || cfg.listId !== this.listId) {
            return false;
        }
        if (this.relation || cfg.relation) {
            const originId: number | null = this.relation ? this.relation.id : null;
            const configId: number | null = cfg.relation ? cfg.relation.id : null;
            if (originId !== configId || cfg.recordId !== this.recordId) {
                return false;
            }
        }
        return true;
    }

    public hasRelationTo(entityId: number): boolean {
        return !!this.relatedEntityIds[entityId];
    }

    /* Private Methods - System */

    private async timeout(): Promise<void> {
        await timeout(500);
    }

    /* Private Methods - Initializations */

    private async init(): Promise<void> {
        await this.loadDocuments();
        if (this.relation && this.recordId) {
            const doc: IDocument | null = await Documents.loadOne(this.relation.ecnTo, this.recordId);
            if (doc) {
                this.recordTitle = Documents.getTitle(doc, this.relation.ecnTo);
            }
            this.notification();
        }
        this.applyView();
        this.notification();
    }

    private getView(viewId: number): IListView {
        const view: IListView | null = ListView.getPrepared(viewId);
        if (view && view.entityId === this.entity.id) {
            return view;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private getViewState(): IListViewState {
        return clone(this.view.state);
    }

    /* Private Methods - State Control */

    private applyView(): void {
        this.state = this.getViewState();
        this.applyFilters();
        this.applySorting();
    }

    private applyFilters(): void {
        const filters: IEntityFilterPrepared[] = CollectionFilters.prepare(this.state.filters);
        const pattern: string = normalize(this.textPattern.toLowerCase().trim());
        const patterns: string[] = uniq(pattern.split(' ')).filter((p: string) => !!p.length);
        this.models.forEach((model: Model) => {
            if (patterns.length) {
                if (!CollectionFilters.filterText(model.getFormatted(), patterns)) {
                    model.filtered = false;
                    return;
                }
            }
            for (let i = 0; i < filters.length; i++) {
                if (!CollectionFilters.filter(model.data, filters[i])) {
                    model.filtered = false;
                    return;
                }
            }
            model.filtered = true;
        });
    }

    private applySorting(): void {
        const sorting: IFieldSortingPrepared[] = CollectionSorting.prepare(this.entity, this.state.sorting);
        this.models.forEach((model: Model) => model.prepareSortingValues(sorting));
        this.models.sort((a: Model, b: Model) => {
            return CollectionSorting.sort(a.data, b.data, sorting);
        });
    }

    /* Private Methods */

    private notification(): void {
        if (this.active) {
            Notification.sendCollectionMessage(this.getClientResponse());
        }
    }

    private async loadDocuments(): Promise<void> {
        let docs: IDocument[];
        if (this.relation && this.recordId) {
            /* Relation table */
            docs = await Documents.loadDocuments(this.entity.id, {
                filters: [{
                    fieldId: this.relation.id,
                    value: [this.recordId]
                }]
            });
        } else {
            /* Regular table */
            docs = await Documents.loadDocuments(this.entity.id, {});
        }
        await Documents.loadRelatedDocs(this.entity, docs);
        this.models = docs.map((doc: IDocument) => new Model(this.entity, doc, this.baseModel));
        Documents.setLinkedFieldValues(this.entity, this.models.map((m: Model) => m.data));
        this.dataLoaded = true;
    }

    private async checkRelations(load: IModelRelatedInfo[]): Promise<void> {
        if (load.length) {
            const relatedData: IModelRelatedData = {};
            const dataByEntity: IGroup<IModelRelatedInfo> = groupBy(load, 'entityId');
            for (let prop in dataByEntity) {
                const entityId: number = Number(prop);
                const ids: number[] = uniq(dataByEntity[entityId].map((i: IModelRelatedInfo) => {
                    return i.recordId;
                })).filter((id: number) => id !== null);
                relatedData[entityId] = [];
                if (ids.length) {
                    relatedData[entityId] = await Documents.loadDocuments(entityId, { ids });
                }
            }
            const dataByModels: IGroup<IModelRelatedInfo> = groupBy(load, 'id');
            for (let prop in dataByModels) {
                const modelId: number = Number(prop);
                const m: Model | null = this.get(modelId);
                if (m) {
                    m.setRelatedData(dataByModels[prop], relatedData);
                }
            }
        }
    }

    private setRelatedEntities(): void {
        const fields: IField[] = Entity.filterFieldsByType(this.entity, [FieldType.linked]);
        this.relatedEntityIds = {};
        fields.forEach((field: IField) => {
            if (!this.relatedEntityIds[field.foreignerEcn]) {
                this.relatedEntityIds[field.foreignerEcn] = [];
            }
            this.relatedEntityIds[field.foreignerEcn].push(field.linkedRelation[0]);
        });
    }

    private getState(): IListViewState {
        this.state.filters.forEach((filter: IEntityFilter) => {
            filter.id = filter.pathId.join(',');
        });
        return this.state;
    }

    private get(id: number): Model | null {
        return this.models.find((m: Model) => m.id === id) || null;
    }

    private clearModelsFeedback(): void {
        this.models.forEach((model: Model) => model.clearFeedback());
    }

}